import { useCallback } from 'react';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { submitImageSearch } from '@/redux/modules/search/imageSearch/imageSearch.actions';
import { clearImageSearchError } from '@/redux/modules/search/imageSearch/imageSearch.reducer';
import {
    getImageSearchError,
    getImageSearchResults,
    getImageSearchStatus,
    getImageSearchThumbnailUrl,
} from '@/redux/modules/search/imageSearch/imageSearch.selectors';

/**
 * Single entry point for the image-search flow. The upload surfaces call `submit(file)` and
 * navigate to the results page on success; the results page reads `results` / `thumbnailUrl`.
 * `submit` resolves the dispatched thunk so callers can branch on `submitImageSearch.fulfilled.match`.
 */
export const useImageSearch = () => {
    const dispatch = useAppDispatch();
    const status = useAppSelector(getImageSearchStatus);
    const error = useAppSelector(getImageSearchError);
    const results = useAppSelector(getImageSearchResults);
    const thumbnailUrl = useAppSelector(getImageSearchThumbnailUrl);

    const submit = useCallback(
        async (file: File) => {
            const previousThumbnailUrl = thumbnailUrl;
            const action = await dispatch(submitImageSearch(file));
            // Revoke the prior object URL only once a new result has replaced it, so the previous
            // thumbnail stays visible while the search is analysing. A failed search keeps the prior
            // results/thumbnail on the page, so its URL must not be revoked here.
            if (
                previousThumbnailUrl &&
                submitImageSearch.fulfilled.match(action) &&
                action.payload.thumbnailUrl !== previousThumbnailUrl
            ) {
                URL.revokeObjectURL(previousThumbnailUrl);
            }
            return action;
        },
        [dispatch, thumbnailUrl]
    );

    const clearError = useCallback(() => {
        dispatch(clearImageSearchError());
    }, [dispatch]);

    return { clearError, error, results, status, submit, thumbnailUrl };
};
